/**
 * Eight fixed groups, rotated at registration (D24, arity revised by D53).
 *
 * World Cup countries, replacing the four card suits. The list is ordered as
 * the draw was written down and **the order is load-bearing**: `pre-token-gen`
 * rotates over these indices, so reordering them silently reassigns everybody
 * who has not yet signed in.
 *
 * This is the only definition. It used to be three — here, `apps/web/src/lib/
 * session.ts`, and a hand-copied literal inside the token trigger — which is
 * exactly how a list drifts. The trigger bundles through esbuild from source,
 * so it can import this like any other consumer, and now does.
 */
export type Team =
  | 'Brazil' | 'Netherlands' | 'Italy' | 'Japan'
  | 'England' | 'Spain' | 'France' | 'Argentina';

export const TEAMS = [
  'Brazil', 'Netherlands', 'Italy', 'Japan',
  'England', 'Spain', 'France', 'Argentina',
] as const satisfies readonly Team[];

/**
 * The team to show when the real one is not available yet.
 *
 * Two callers since D55, and neither is the diagnostic this used to be. The
 * client shows it while `GET /me` is in flight on a first-ever load with no
 * cached profile; `/photos/complete` uses it if the guest row has gone, where
 * attributing a photograph oddly beats losing it.
 *
 * It is still named rather than written inline, because a literal repeated
 * across files is how the two drift apart.
 */
export const TEAM_FALLBACK: Team = 'Brazil';

/**
 * Which team the `index`-th guest to sign in joins (D24).
 *
 * **Rotation, not a draw.** With staggered arrivals, independent random draws
 * give lopsided groups — sizes decided by check-in order. Walking a rotation
 * keeps every team within one of every other, whatever order people arrive in,
 * and costs a single atomic `ADD 1` on the counter with no read.
 *
 * The order reshuffles each full cycle, seeded on the cycle number, which buys
 * the two properties D24 is actually after: consecutive indices land on
 * different teams — so a couple checking in together is split, which is the
 * point of an ice-breaker — and nobody can predict their team from the person
 * ahead of them in the queue.
 *
 * **The split is not guaranteed across a cycle boundary.** Each cycle is
 * shuffled on its own seed, so cycle N can open on the team cycle N-1 closed
 * with, and the one pair straddling that seam shares a team — a 1-in-8 chance
 * at each seam, where the four suits made it 1-in-4. Left alone deliberately:
 * closing it means each cycle consulting the previous one's final order, which
 * is recursive back to cycle zero and wants a memo to stay cheap. That is a
 * fair amount of machinery to buy an outcome that, at two hundred guests, is
 * one or two couples who were going to be split up by the seating plan anyway.
 * `identity.test.ts` asserts it happens *only* at seams, so it cannot quietly
 * spread.
 *
 * It lives beside `TEAMS` rather than in the token trigger that calls it
 * because it is meaningless apart from that list: the arity is `TEAMS.length`,
 * and the two going out of step is precisely the bug worth preventing. The
 * trigger had its own copy of both when the teams were suits.
 */
export function teamForIndex(index: number): Team {
  const cycle = Math.floor(index / TEAMS.length);
  return orderForCycle(cycle)[index % TEAMS.length]!;
}

/** Deterministic per cycle, so every invocation agrees without coordinating.
 *  The +1 keeps cycle 0 from seeding at zero, where the first draw would
 *  always be the same value. */
function orderForCycle(cycle: number): Team[] {
  const order = [...TEAMS] as Team[];
  // Fisher-Yates, seeded on the cycle number.
  let seed = ((cycle + 1) * 2654435761) % 4294967296;
  const rnd = () => (seed = (seed * 1103515245 + 12345) % 2147483648) / 2147483648;
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return order;
}

export type AuthProvider = 'google' | 'email' | 'admin';

/**
 * A guest record. `userId` is the Cognito `sub` and is the identity everything
 * else hangs off — ownership is the verified identity (D44).
 */
export interface Guest {
  userId: string;
  email: string;
  displayName: string;
  team: Team;
  registeredAt: number;
  /**
   * The roster row this guest holds (D59). Absent until they claim one; the
   * pointer half of a two-write latch whose other half is `roster.claimedBy`.
   */
  guestId?: string;
  /**
   * Set when the guest confirms their name on the welcome screen. Its absence
   * is what `SessionInfo.needsWelcome` reports, so the screen shows once and
   * not again. Written by `identity`, never by the token trigger.
   */
  welcomedAt?: number;
}

/**
 * The longest display name the API will store.
 *
 * It rides on every photo tile under a thumbnail half a phone wide, where
 * `.tile-name` already ellipsises — so this is not a layout constraint, it is a
 * storage one: the field is denormalised onto every row the guest uploads, and
 * an unbounded string there is unbounded on all of them.
 */
export const MAX_DISPLAY_NAME = 40;

/**
 * What `GET /me` returns — the profile the **server** owns.
 *
 * This interface existed before the route did, and described something else:
 * "assembled from token claims alone", on D17's argument that no handler should
 * read the database to authorise a request. D55 separated those two ideas.
 * Authorisation still never reads the database — `sub` and `cognito:groups` are
 * in the token and always were. `displayName` and `team` are not
 * authorisation, they are mutable profile, and a token is the wrong place to
 * keep something that changes.
 *
 * `provider` and `needsWelcome` are deliberately **not** here. The first is
 * derivable from the token's `identities` and the second is a one-time local
 * nicety (see `session.ts`); neither is the server's to state, and returning a
 * guess for either would make this response the authority on something it
 * cannot see.
 */
export interface SessionInfo {
  userId: string;
  email: string;
  displayName: string;
  team: Team;
  isAdmin: boolean;
}

/**
 * `PATCH /me`. A guest may change their own display name and nothing else —
 * team is an organiser's to move (D54), and everything else on the row is
 * either identity or bookkeeping.
 */
export interface PatchMeRequest {
  displayName: string;
}

/**
 * What the JWT authorizer hands a handler.
 *
 * `sub` and `cognito:groups` are the whole of it in practice: identity and
 * authorisation, fixed for the life of the session, readable without touching
 * the database (D17).
 *
 * The two `custom:` claims are **vestigial** (D55). `pre-token-gen` still writes
 * them and nothing reads them; they are optional here so that no handler can
 * start depending on their presence again, and they are declared at all only
 * because they are genuinely still on the wire until the trigger stops stamping
 * them. `docs/todo.md` carries the deletion.
 */
export interface TokenClaims {
  sub: string;
  email: string;
  'cognito:groups'?: string[];
  /** @deprecated Vestigial since D55 — profile comes from `GET /me`. */
  'custom:displayName'?: string;
  /** @deprecated Vestigial since D55 — profile comes from `GET /me`. */
  'custom:team'?: Team;
  /**
   * The roster row this caller holds (D59). **Authorisation, not profile** —
   * its absence is what "has not claimed a name yet" means, and it is what the
   * gate checks. Optional because it is genuinely absent until a guest claims,
   * which is every guest until they do.
   */
  'custom:guestId'?: string;
}

export const ADMIN_GROUP = 'admin';

/**
 * One row of the admin user dashboard (D48).
 *
 * `lastSeen` is deliberately not called "last login": the pre-token-generation
 * trigger that records it fires on silent refreshes too, so it is the last time
 * the app was in use rather than the last time somebody typed a code. That is
 * the more useful of the two for an organiser looking at a screen mid-event,
 * but it must never be labelled as a login.
 */
export interface GuestSummary {
  userId: string;
  email: string;
  displayName: string;
  team: Team;
  isAdmin: boolean;
  uploads: number;
  registeredAt: number;
  lastSeen?: number;
}

export interface GuestsResponse {
  guests: GuestSummary[];
  /** Every upload by everyone — the total an organiser is asking about. Since
   *  D50 nothing can be held back from the gallery, so there is no second,
   *  smaller number for this to be confused with. */
  totalUploads: number;
}
